import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  payoutSyncWindow,
  routes,
  type MarketplaceId,
  type PayoutBalancesResponse,
  type PayoutFleetResponse,
  type PayoutSyncResponse,
} from "@nova-ai/shared"
import { apiFetch } from "../lib/api"

/**
 * Every configured marketplace's mirrored payout history, in one request.
 *
 * Takes no window: each block shows its newest cycles and expands to everything on
 * demand, so the whole history arrives once rather than a request per expand — a
 * year is ~36 cycles per marketplace, and an expand that has to wait for the
 * network is an expand that feels broken.
 */
export function usePayoutFleet() {
  return useQuery({
    queryKey: ["payouts", "fleet"] as const,
    queryFn: async () => {
      const result = await apiFetch<PayoutFleetResponse>(routes.marketplaces.payoutsFleet())
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

/** Every marketplace's account position as of the newest snapshot, read
 *  separately from the cycles because it answers a different question — "where do
 *  we stand now" rather than "what has been paid". The daily sweep is what moves
 *  these numbers; a block's Refresh button only touches the cycle mirror, which is
 *  why this key sits outside the cycle prefix. */
export function usePayoutBalances() {
  return useQuery({
    queryKey: ["payout-balances"] as const,
    queryFn: async () => {
      const result = await apiFetch<PayoutBalancesResponse>(
        routes.marketplaces.payoutBalancesFleet()
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

/**
 * Refresh one marketplace's authoritative cycle mirror over the same window the
 * scheduled sweep covers, resolved from the clock here.
 *
 * Per marketplace, never fleet-wide: Mirakl recommends this billing-cycle read at
 * most once daily and the 07:45 sweep already owns freshness, so a manual refresh
 * stays a deliberate act on one marketplace.
 */
export function usePayoutsSync(marketplace: MarketplaceId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await apiFetch<PayoutSyncResponse>(
        routes.marketplaces.payoutsSync({ marketplace }),
        { method: "POST", body: payoutSyncWindow(new Date()) }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payouts", "fleet"] })
    },
  })
}
